import { jsPDF } from "jspdf";
import autoTable, { type CellHookData } from "jspdf-autotable";
import JSZip from "jszip";
import { parseSelectedTaxLines, reconcileTaxLineIds, computeDocumentTotals, type TaxLine } from "@workspace/totals";

type ColumnConfig = {
  description: { visible: boolean; label: string };
  qty: { visible: boolean; label: string };
  unitPrice: { visible: boolean; label: string };
  amount: { visible: boolean; label: string };
};

export type TemplateSettings = {
  accentColor?: string | null;
  documentTitle?: string | null;
  columnConfig?: string | null;
  taxLines?: string | null;
  currencyLabel?: string | null;
  termsAndNotes?: string | null;
  invoiceDocumentTitle?: string | null;
  invoiceAccentColor?: string | null;
  invoiceCurrencyLabel?: string | null;
  invoiceColumnConfig?: string | null;
  invoiceTermsAndNotes?: string | null;
  pdfFooter?: string | null;
  invoicePdfFooter?: string | null;
  timezone?: string | null;
};

export type CompanySettings = {
  companyName?: string | null;
  address?: string | null;
  addressLine1?: string | null;
  addressLine2?: string | null;
  city?: string | null;
  state?: string | null;
  postalCode?: string | null;
  country?: string | null;
  phone?: string | null;
  email?: string | null;
  website?: string | null;
  tagline?: string | null;
  logoBase64?: string | null;
} & TemplateSettings;

export type EstimateData = {
  id: number;
  estimateNumber?: string | null;
  title: string;
  status: string;
  customerName?: string | null;
  customerPhone?: string | null;
  customerEmail?: string | null;
  customerAddressLine1?: string | null;
  customerAddressLine2?: string | null;
  customerCity?: string | null;
  customerState?: string | null;
  customerPostalCode?: string | null;
  customerCountry?: string | null;
  description?: string | null;
  total: number;
  subtotal?: number;
  taxAmount?: number;
  taxEnabled?: boolean;
  taxRate?: number;
  selectedTaxLines?: string | null;
  internalCost?: number;
  margin?: number;
  profit?: number;
  depositType?: "fixed" | "percent" | null;
  depositValue?: number | null;
  discountType?: "fixed" | "percent" | null;
  discountValue?: number | null;
  discountAmount?: number;
  estimateDate?: string | null;
  validUntil?: string | null;
  createdAt: string;
  isRecurring?: boolean | null;
  recurringInterval?: string | null;
  recurringCustomDays?: number | null;
  recurringStartDate?: string | null;
  recurringEndDate?: string | null;
  isMilestoneEstimate?: boolean | null;
  termsAndNotes?: string | null;
  milestones?: Array<{
    name: string;
    percentage?: number | null;
    amount?: number | null;
    status?: string | null;
  }>;
  lineItems: Array<{
    description: string;
    productName?: string | null;
    quantity: number;
    unitPrice: number;
    total: number;
    privateNote?: string | null;
    internalCostTotal?: number;
    groupId?: number | null;
    materials?: Array<{
      id: number;
      name: string;
      quantity: number;
      unit?: string | null;
      unitCost: number;
      totalCost: number;
      type?: string | null;
      category?: string | null;
    }>;
  }>;
  groups?: Array<{ id: number; name: string; sortOrder: number }>;
};

const DEFAULT_COL_CONFIG: ColumnConfig = {
  description: { visible: true, label: "Services" },
  qty: { visible: true, label: "Qty" },
  unitPrice: { visible: true, label: "Unit Price" },
  amount: { visible: true, label: "Amount" },
};

function parseColumnConfig(raw: string | null | undefined): ColumnConfig {
  if (!raw) return DEFAULT_COL_CONFIG;
  try {
    const parsed = JSON.parse(raw);
    return {
      description: parsed.description ?? DEFAULT_COL_CONFIG.description,
      qty: parsed.qty ?? DEFAULT_COL_CONFIG.qty,
      unitPrice: parsed.unitPrice ?? DEFAULT_COL_CONFIG.unitPrice,
      amount: parsed.amount ?? DEFAULT_COL_CONFIG.amount,
    };
  } catch {
    return DEFAULT_COL_CONFIG;
  }
}

const parseTaxLines = parseSelectedTaxLines;

function buildAddressLines(company: CompanySettings): string[] {
  const hasDistinctFields = company.addressLine1 || company.addressLine2 || company.city || company.state || company.postalCode || company.country;
  if (hasDistinctFields) {
    const lines: string[] = [];
    if (company.addressLine1) lines.push(company.addressLine1);
    if (company.addressLine2) lines.push(company.addressLine2);
    const cityStateLine = [company.city, company.state].filter(Boolean).join(", ");
    const cityStatePostal = [cityStateLine, company.postalCode].filter(Boolean).join(" ");
    if (cityStatePostal) lines.push(cityStatePostal);
    if (company.country) lines.push(company.country);
    return lines;
  }
  if (company.address) {
    return company.address.split("\n").map(l => l.trim()).filter(Boolean);
  }
  return [];
}

function hexToRgb(hex: string): [number, number, number] {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? [parseInt(result[1], 16), parseInt(result[2], 16), parseInt(result[3], 16)]
    : [30, 58, 95];
}

function formatCurrencyWithLabel(value: number, currencyLabel?: string | null): string {
  const label = currencyLabel?.trim() || "USD";
  let formatted: string;
  try {
    formatted = new Intl.NumberFormat("en-US", { style: "currency", currency: label }).format(value);
  } catch {
    try {
      formatted = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(value);
      formatted = `${label} ${formatted.replace(/^[A-Z$€£¥]+/, "").trim()}`;
    } catch {
      formatted = `$${value.toFixed(2)}`;
    }
  }
  return formatted;
}


function parseDateStr(dateStr: string): Date {
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    const [y, m, d] = dateStr.split("-").map(Number);
    return new Date(y, m - 1, d);
  }
  return new Date(dateStr);
}

function formatDateWithTimezone(dateStr: string, timezone?: string | null): string {
  const opts: Intl.DateTimeFormatOptions = { year: "numeric", month: "long", day: "numeric" };
  try {
    const date = parseDateStr(dateStr);
    if (timezone) {
      return new Intl.DateTimeFormat("en-US", { ...opts, timeZone: timezone }).format(date);
    }
    return date.toLocaleDateString("en-US", opts);
  } catch {
    return new Date(dateStr).toLocaleDateString("en-US", opts);
  }
}

const formatDate = (dateStr: string) =>
  parseDateStr(dateStr).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });

function addValidUntil(dateStr: string, days = 30, timezone?: string | null): string {
  const d = new Date(dateStr);
  d.setDate(d.getDate() + days);
  return formatDateWithTimezone(d.toISOString(), timezone);
}

const FOOTER_RESERVE = 28;

/**
 * Keeps a group heading row on the same page as the row after it. Call from
 * didParseCell for a heading row's cells (with rowPageBreak: "avoid").
 *
 * jspdf-autotable has no keep-with-next option, so this wraps the row's
 * canEntireRowFit check (used when deciding whether a row fits on the
 * current page) to also require room for the next row. If that next row is
 * taller than a full page it would be split anyway, so it is ignored.
 */
function keepRowWithNext(data: CellHookData, doc: jsPDF, bottomMargin: number): void {
  type FitRow = { index: number; canEntireRowFit?: (h: number, cols: unknown) => boolean; getMaxCellHeight?: (cols: unknown) => number; __keepWithNext?: boolean };
  const row = data.row as unknown as FitRow;
  if (row.__keepWithNext || typeof row.canEntireRowFit !== "function") return;
  row.__keepWithNext = true;
  const table = data.table as unknown as { body?: FitRow[]; getHeadHeight?: (cols: unknown) => number; settings?: { margin?: { top: number } } };
  const ownFit = row.canEntireRowFit.bind(row);
  row.canEntireRowFit = (height, cols) => {
    if (!ownFit(height, cols)) return false;
    const next = table.body?.[row.index + 1];
    if (!next?.getMaxCellHeight || !row.getMaxCellHeight) return true;
    const pageSpace = doc.internal.pageSize.getHeight() - (table.settings?.margin?.top ?? 0) - bottomMargin - (table.getHeadHeight?.(cols) ?? 0);
    const both = row.getMaxCellHeight(cols) + next.getMaxCellHeight(cols);
    return both <= height || both > pageSpace;
  };
}

const intervalLabels: Record<string, string> = {
  weekly: "Weekly",
  biweekly: "Bi-weekly",
  monthly: "Monthly",
  quarterly: "Quarterly",
  semiannually: "Semi-annually",
  annually: "Annually",
  custom: "Custom",
  first_of_month: "Monthly",
  fifteenth_of_month: "Monthly",
  last_of_month: "Monthly",
  custom_months: "Monthly",
  custom_days: "Custom",
};

function formatRecurringInterval(interval: string | null | undefined, customDays?: number | null): string {
  if (interval === "custom_days" && customDays && customDays > 0) {
    return `Every ${customDays} days`;
  }
  return intervalLabels[interval ?? "monthly"] ?? "Monthly";
}

type RecurringSectionData = {
  recurringInterval?: string | null;
  recurringCustomDays?: number | null;
  recurringStartDate?: string | null;
  recurringEndDate?: string | null;
};

function renderRecurringSection(
  doc: jsPDF,
  startY: number,
  margin: number,
  rightX: number,
  pageHeight: number,
  estimate: RecurringSectionData,
  grandTotal: number,
  accentRgb: [number, number, number],
  fmt: (v: number) => string,
  fmtDate: (d: string) => string,
): number {
  let ty = startY;
  if (ty > pageHeight - FOOTER_RESERVE - 40) {
    doc.addPage();
    ty = margin;
  }
  doc.setDrawColor(200, 200, 200);
  doc.setLineWidth(0.3);
  doc.line(margin, ty, rightX, ty);
  ty += 6;

  const intervalLabel = formatRecurringInterval(estimate.recurringInterval, estimate.recurringCustomDays);

  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.setTextColor(30, 30, 30);
  doc.text("Recurring Schedule", margin, ty);
  ty += 3;

  const body: string[][] = [];
  body.push(["Billing Cycle", intervalLabel]);
  if (estimate.recurringStartDate) {
    body.push(["Start Date", fmtDate(estimate.recurringStartDate)]);
  }
  if (estimate.recurringEndDate) {
    body.push(["End Date", fmtDate(estimate.recurringEndDate)]);
  }
  body.push(["Per-cycle Amount", fmt(grandTotal)]);

  autoTable(doc, {
    startY: ty + 2,
    head: [["Detail", "Value"]],
    body,
    margin: { left: margin, right: margin, bottom: FOOTER_RESERVE },
    styles: { fontSize: 9, cellPadding: { top: 3, bottom: 3, left: 4, right: 4 }, textColor: [30, 30, 30], lineColor: [220, 220, 220], lineWidth: 0 },
    headStyles: {
      fillColor: accentRgb,
      textColor: [255, 255, 255],
      fontStyle: "bold",
      fontSize: 9,
      cellPadding: { top: 2, right: 4, bottom: 2, left: 4 },
    },
    columnStyles: {
      0: { cellWidth: "auto" },
      1: { cellWidth: 50, halign: "right" },
    },
    theme: "plain",
    didParseCell: (data: CellHookData) => {
      if (data.section === "body") {
        data.cell.styles.lineWidth = { top: 0, bottom: 0.2, left: 0, right: 0 };
        data.cell.styles.lineColor = [220, 220, 220];
      }
    },
  });

  return (doc as any).lastAutoTable.finalY + 8;
}

function renderMilestonesSection(
  doc: jsPDF,
  startY: number,
  margin: number,
  rightX: number,
  pageHeight: number,
  milestones: NonNullable<EstimateData["milestones"]>,
  grandTotal: number,
  accentRgb: [number, number, number],
  fmt: (v: number) => string,
): number {
  let ty = startY;
  if (ty > pageHeight - FOOTER_RESERVE - 40) {
    doc.addPage();
    ty = margin;
  }
  doc.setDrawColor(200, 200, 200);
  doc.setLineWidth(0.3);
  doc.line(margin, ty, rightX, ty);
  ty += 6;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.setTextColor(30, 30, 30);
  doc.text("Payment Milestones", margin, ty);
  ty += 3;

  const body = milestones.map(m => {
    const hasPct = m.percentage !== null && m.percentage !== undefined && Number(m.percentage) > 0;
    const hasAmt = m.amount !== null && m.amount !== undefined && Number(m.amount) > 0;
    const amt = hasPct
      ? grandTotal * (Number(m.percentage) / 100)
      : (hasAmt ? Number(m.amount) : 0);
    const amountLabel = hasPct
      ? `${Number(m.percentage)}% — ${fmt(amt)}`
      : fmt(amt);
    const statusRaw = (m.status || "pending").toString();
    const statusLabel = statusRaw.charAt(0).toUpperCase() + statusRaw.slice(1);
    return [m.name || "—", amountLabel, statusLabel];
  });

  autoTable(doc, {
    startY: ty + 2,
    head: [["Milestone", "Amount", "Status"]],
    body,
    margin: { left: margin, right: margin, bottom: FOOTER_RESERVE },
    styles: { fontSize: 9, cellPadding: { top: 3, bottom: 3, left: 4, right: 4 }, textColor: [30, 30, 30], lineColor: [220, 220, 220], lineWidth: 0 },
    headStyles: {
      fillColor: accentRgb,
      textColor: [255, 255, 255],
      fontStyle: "bold",
      fontSize: 9,
      cellPadding: { top: 2, right: 4, bottom: 2, left: 4 },
    },
    columnStyles: {
      0: { cellWidth: "auto" },
      1: { cellWidth: 50, halign: "right" },
      2: { cellWidth: 28, halign: "center" },
    },
    theme: "plain",
    didParseCell: (data: CellHookData) => {
      if (data.section === "body") {
        data.cell.styles.lineWidth = { top: 0, bottom: 0.2, left: 0, right: 0 };
        data.cell.styles.lineColor = [220, 220, 220];
      }
    },
  });

  return (doc as any).lastAutoTable.finalY + 8;
}

export function buildCustomerPDFDoc(estimate: EstimateData, company: CompanySettings = {}): jsPDF {
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "letter" });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 18;

  const accentRgb = hexToRgb(company.accentColor || "#1e3a5f");
  const docTitle = company.documentTitle || "Quotation";
  const currencyLabel = company.currencyLabel || "USD";
  const colConfig = parseColumnConfig(company.columnConfig);
  const estimateDepositType = estimate.depositType ?? "percent";
  const estimateDepositValue = estimate.depositValue ?? 0;

  const estimateTaxEnabled = estimate.taxEnabled ?? false;
  const estimateTaxRate = estimate.taxRate ?? 0;
  const estimateSelectedTaxLines = parseTaxLines(estimate.selectedTaxLines);

  const fmt = (v: number) => formatCurrencyWithLabel(v, currencyLabel);
  const fmtDate = (dateStr: string) => formatDateWithTimezone(dateStr, company.timezone);

  let y = margin;

  const rightX = pageWidth - margin;

  const maxLogoW = 50;
  const maxLogoH = 28;
  let logoW = maxLogoW;
  let logoH = maxLogoH;

  if (company.logoBase64) {
    try {
      const props = doc.getImageProperties(company.logoBase64);
      const scale = Math.min(maxLogoW / props.width, maxLogoH / props.height);
      logoW = props.width * scale;
      logoH = props.height * scale;
      doc.addImage(company.logoBase64, "PNG", margin, y, logoW, logoH);
    } catch {
    }
  }

  doc.setFont("helvetica", "normal");
  doc.setFontSize(36);
  doc.setTextColor(30, 30, 30);
  doc.text(docTitle, rightX, y + 10, { align: "right" });

  const companyInfoY = y + 16;
  const companyLines: string[] = [];
  if (company.companyName) companyLines.push(company.companyName);
  buildAddressLines(company).forEach(l => companyLines.push(l));
  if (company.phone) companyLines.push(company.phone);
  if (company.website) companyLines.push(company.website);
  if (company.email) companyLines.push(company.email);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(8.5);
  doc.setTextColor(50, 50, 50);
  companyLines.forEach((line, i) => {
    if (i === 0) {
      doc.setFont("helvetica", "bold");
      doc.setFontSize(10);
    } else {
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8.5);
    }
    doc.text(line, rightX, companyInfoY + i * 4.8, { align: "right" });
  });

  y = Math.max(y + logoH + 6, companyInfoY + companyLines.length * 4.8 + 4);

  doc.setDrawColor(180, 180, 180);
  doc.setLineWidth(0.4);
  doc.line(margin, y, rightX, y);
  y += 8;

  const billToX = margin;
  const metaX = pageWidth / 2 + 10;
  const billToStartY = y;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(7.5);
  doc.setTextColor(120, 120, 120);
  doc.text("BILL TO", billToX, billToStartY);

  let btY = billToStartY + 5;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.setTextColor(20, 20, 20);
  doc.text(estimate.customerName || "—", billToX, btY);
  btY += 5;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(60, 60, 60);
  if (estimate.customerPhone) { doc.text(estimate.customerPhone, billToX, btY); btY += 5; }
  if (estimate.customerEmail) { doc.text(estimate.customerEmail, billToX, btY); btY += 5; }
  if (estimate.customerAddressLine1) { doc.text(estimate.customerAddressLine1, billToX, btY); btY += 5; }
  if (estimate.customerAddressLine2) { doc.text(estimate.customerAddressLine2, billToX, btY); btY += 5; }
  const cityStateLine = [estimate.customerCity, estimate.customerState].filter(Boolean).join(", ");
  const postalCountryLine = [estimate.customerPostalCode, estimate.customerCountry].filter(Boolean).join(" ");
  if (cityStateLine) { doc.text(cityStateLine, billToX, btY); btY += 5; }
  if (postalCountryLine) { doc.text(postalCountryLine, billToX, btY); btY += 5; }

  const subtotal = estimate.lineItems.reduce((s, li) => s + li.total, 0);
  const estimateDiscountType = estimate.discountType ?? null;
  const estimateDiscountValue = estimate.discountValue ?? null;
  const { discountAmount, taxableBase, taxAmount, total: grandTotal } = computeDocumentTotals({
    subtotal,
    discountType: estimateDiscountType,
    discountValue: estimateDiscountValue,
    taxEnabled: estimateTaxEnabled,
    taxRate: estimateTaxRate,
    selectedTaxLines: estimate.selectedTaxLines,
  });
  const activeTaxLines = estimateTaxEnabled
    ? reconcileTaxLineIds(estimateSelectedTaxLines, company.taxLines)
    : [];
  const hasMilestones = (estimate.milestones?.length ?? 0) > 0;
  const depositAmount = estimateDepositValue > 0
    ? (estimateDepositType === "fixed" ? estimateDepositValue : grandTotal * (estimateDepositValue / 100))
    : 0;

  const metaRows: Array<{ label: string; value: string; highlight?: boolean }> = [
    { label: "Quote Number:", value: estimate.estimateNumber ? `#${estimate.estimateNumber}` : `#${estimate.id}` },
    { label: "Quote Date:", value: estimate.estimateDate ? fmtDate(estimate.estimateDate) : fmtDate(estimate.createdAt) },
    ...(estimate.validUntil ? [{ label: "Valid Until:", value: fmtDate(estimate.validUntil) }] : []),
    { label: `Grand Total (${currencyLabel}):`, value: fmt(grandTotal), highlight: true },
  ];
  if (estimateDepositValue > 0 && !hasMilestones) {
    const depositLabel = estimateDepositType === "percent"
      ? `Deposit Requested (${estimateDepositValue}%)`
      : "Deposit Requested";
    metaRows.push({ label: depositLabel, value: fmt(depositAmount) });
  }

  const metaRowH = 7;
  const metaColW = rightX - metaX;
  metaRows.forEach((row, i) => {
    const ry = billToStartY + i * metaRowH;
    if (row.highlight) {
      doc.setFillColor(240, 240, 240);
      doc.rect(metaX - 2, ry - 4.5, metaColW + 2, metaRowH, "F");
    }
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8.5);
    doc.setTextColor(30, 30, 30);
    doc.text(row.label, rightX - 40, ry, { align: "right" });
    doc.setFont("helvetica", row.highlight ? "bold" : "normal");
    doc.setFontSize(8.5);
    doc.text(row.value, rightX, ry, { align: "right" });
  });

  y = Math.max(btY + 4, billToStartY + metaRows.length * metaRowH + 4);
  y += 4;

  const tableHead: string[] = [];
  const colWidths: Record<number, object> = {};
  let colIdx = 0;

  const anyVisible = colConfig.description.visible || colConfig.qty.visible ||
    colConfig.unitPrice.visible || colConfig.amount.visible;
  const effectiveCol = anyVisible ? colConfig : {
    ...colConfig,
    description: { ...colConfig.description, visible: true },
  };

  if (effectiveCol.description.visible) {
    tableHead.push(effectiveCol.description.label);
    colWidths[colIdx] = { cellWidth: "auto" };
    colIdx++;
  }
  if (effectiveCol.qty.visible) {
    tableHead.push(effectiveCol.qty.label);
    colWidths[colIdx] = { cellWidth: 18, halign: "center" };
    colIdx++;
  }
  if (effectiveCol.unitPrice.visible) {
    tableHead.push(effectiveCol.unitPrice.label);
    colWidths[colIdx] = { cellWidth: 32, halign: "right" };
    colIdx++;
  }
  if (effectiveCol.amount.visible) {
    tableHead.push(effectiveCol.amount.label);
    colWidths[colIdx] = { cellWidth: 32, halign: "right", fontStyle: "bold" };
    colIdx++;
  }

  const descColIdx = effectiveCol.description.visible ? 0 : -1;
  const numCols = tableHead.length;

  const sortedGroups = [...(estimate.groups ?? [])].sort((a, b) => a.sortOrder - b.sortOrder);
  const groupMap = new Map(sortedGroups.map(g => [g.id, g]));

  type BodyRow = string[] | Array<{ content: string; colSpan?: number; styles?: object }>;
  type RowMeta = { kind: "item"; item: typeof estimate.lineItems[0] } | { kind: "group"; name: string };
  const tableBody: BodyRow[] = [];
  const rowMeta: RowMeta[] = [];
  let activeGroupName: string | null = null;
  let activeGroupPage: number = 0;

  const buildItemRow = (item: typeof estimate.lineItems[0]): string[] => {
    const row: string[] = [];
    if (effectiveCol.description.visible) row.push(item.productName ? "" : item.description);
    if (effectiveCol.qty.visible) row.push(item.quantity.toString());
    if (effectiveCol.unitPrice.visible) row.push(fmt(item.unitPrice));
    if (effectiveCol.amount.visible) row.push(fmt(item.total));
    return row;
  };

  const ungrouped = estimate.lineItems.filter(li => !li.groupId || !groupMap.has(li.groupId));
  for (const item of ungrouped) {
    tableBody.push(buildItemRow(item));
    rowMeta.push({ kind: "item", item });
  }

  for (const group of sortedGroups) {
    const groupItems = estimate.lineItems.filter(li => li.groupId === group.id);
    if (groupItems.length === 0) continue;
    const groupTotal = groupItems.reduce((s, item) => s + item.total, 0);
    const groupHeaderStyles = { fontStyle: "bold" as const, fillColor: [220, 225, 235] as [number, number, number], textColor: [30, 30, 30] as [number, number, number], cellPadding: { top: 4, bottom: 4, left: 6, right: 4 } };
    const groupHeaderRow = effectiveCol.amount.visible
      ? [
          { content: group.name, colSpan: numCols - 1, styles: groupHeaderStyles },
          { content: fmt(groupTotal), styles: { ...groupHeaderStyles, textColor: [100, 100, 100] as [number, number, number], halign: "right" as const, cellPadding: { top: 4, bottom: 4, left: 4, right: 4 } } },
        ]
      : [{ content: group.name, colSpan: numCols, styles: groupHeaderStyles }];
    tableBody.push(groupHeaderRow);
    rowMeta.push({ kind: "group", name: group.name });
    for (const item of groupItems) {
      tableBody.push(buildItemRow(item));
      rowMeta.push({ kind: "item", item });
    }
  }

  autoTable(doc, {
    startY: y,
    head: [tableHead],
    body: tableBody,
    styles: { fontSize: 9.5, cellPadding: { top: 4, bottom: 4, left: 4, right: 4 }, textColor: [20, 20, 20], lineColor: [220, 220, 220], lineWidth: 0 },
    headStyles: {
      fillColor: accentRgb,
      textColor: [255, 255, 255],
      fontStyle: "bold",
      fontSize: 9.5,
      cellPadding: { top: 2, right: 4, bottom: 2, left: 4 },
    },
    columnStyles: colWidths,
    margin: { left: margin, right: margin, bottom: FOOTER_RESERVE },
    // Product rows draw their text manually in didDrawCell, so a row split
    // across pages would overflow the footer and leave an empty remainder
    // row on the next page. Move the whole row to the next page instead.
    rowPageBreak: "avoid",
    theme: "plain",
    tableLineWidth: 0,
    didParseCell: (data: CellHookData) => {
      if (data.section === "body") {
        const meta = rowMeta[data.row.index];
        if (meta?.kind === "group") {
          data.cell.styles.lineWidth = { top: 0, bottom: 0, left: 0, right: 0 };
          keepRowWithNext(data, doc, FOOTER_RESERVE);
          return;
        }
        data.cell.styles.lineWidth = { top: 0, bottom: 0.2, left: 0, right: 0 };
        data.cell.styles.lineColor = [220, 220, 220];
        if (data.column.index === descColIdx && meta?.kind === "item") {
          const item = meta.item;
          if (item?.productName) {
            const pad = 4;
            const pw = doc.internal.pageSize.getWidth();
            const fixedWidths = Object.values(colWidths).reduce((sum, s) => {
              const w = (s as Record<string, unknown>).cellWidth;
              return sum + (typeof w === "number" ? w : 0);
            }, 0);
            const cellW = pw - margin * 2 - fixedWidths;
            const textW = cellW - pad * 2;
            doc.setFont("helvetica", "bold");
            doc.setFontSize(9.5);
            const nameLines = doc.splitTextToSize(item.productName, textW);
            doc.setFont("helvetica", "normal");
            doc.setFontSize(9.5);
            const descLines = doc.splitTextToSize(item.description, textW);
            const lineHeight = 5;
            const contentHeight = pad + 2.5 + (nameLines.length + descLines.length) * lineHeight + pad;
            data.cell.styles.minCellHeight = contentHeight;
          }
        }
      }
      if (data.section === "head") {
        data.cell.styles.lineWidth = 0;
      }
    },
    didDrawCell: (data: CellHookData) => {
      if (data.section === "body") {
        const meta = rowMeta[data.row.index];
        if (meta?.kind === "group") { activeGroupName = meta.name; activeGroupPage = data.pageNumber; }
        if (meta?.kind === "item" && data.column.index === numCols - 1) {
          const nextMeta = rowMeta[data.row.index + 1];
          if (!nextMeta || nextMeta.kind === "group") activeGroupName = null;
        }
        if (data.column.index === descColIdx) {
          if (meta?.kind !== "item") return;
          const item = meta.item;
          if (item?.productName) {
            const { x, y: cy, width, height } = data.cell;
            const pad = 4;
            const fillW = width - 1;
            const fillH = height - 1;
            doc.setFillColor(255, 255, 255);
            doc.rect(x + 0.5, cy + 0.5, fillW, fillH, "F");
            doc.setFont("helvetica", "bold");
            doc.setFontSize(9.5);
            doc.setTextColor(20, 20, 20);
            const nameLines = doc.splitTextToSize(item.productName, width - pad * 2);
            const lineHeight = 5;
            nameLines.forEach((line: string, i: number) => {
              doc.text(line, x + pad, cy + pad + 2.5 + i * lineHeight);
            });
            doc.setFont("helvetica", "normal");
            doc.setFontSize(9.5);
            doc.setTextColor(20, 20, 20);
            const descLines = doc.splitTextToSize(item.description, width - pad * 2);
            descLines.forEach((line: string, i: number) => {
              doc.text(line, x + pad, cy + pad + 2.5 + nameLines.length * lineHeight + i * lineHeight);
            });
          }
        }
      }
    },
    didDrawPage: (data: any) => {
      if (data.pageNumber > 1 && activeGroupName !== null && data.cursor !== null && data.pageNumber !== activeGroupPage) {
        const cur = data.cursor as { x: number; y: number };
        const pw = doc.internal.pageSize.getWidth();
        const headingH = 9;
        doc.setFillColor(220, 225, 235);
        doc.rect(margin, cur.y, pw - margin * 2, headingH, "F");
        doc.setFont("helvetica", "bold");
        doc.setFontSize(9.5);
        doc.setTextColor(30, 30, 30);
        doc.text(`${activeGroupName} (continued)`, margin + 6, cur.y + 6);
        doc.setFont("helvetica", "normal");
        doc.setTextColor(20, 20, 20);
        cur.y += headingH + 1;
      }
    },
  });

  let ty = (doc as any).lastAutoTable.finalY + 6;

  const estimatedTotalsHeight = (
    (estimateTaxEnabled ? (1 + (activeTaxLines.length > 0 ? activeTaxLines.length : 1)) : 0) + 1 +
    (estimateDepositValue > 0 && !hasMilestones ? 1 : 0)
  ) * 7 + 8;
  if (ty + estimatedTotalsHeight > pageHeight - FOOTER_RESERVE) {
    doc.addPage();
    ty = margin;
  }

  const totalsX = pageWidth / 2 + 10;
  const totalsW = rightX - totalsX;
  const totalsRowH = 7;

  const totalsRows: Array<{ label: string; value: string; bold?: boolean; separator?: boolean }> = [];
  const hasDiscount = discountAmount > 0;
  const showBreakdown = estimateTaxEnabled || hasDiscount;
  if (showBreakdown) {
    totalsRows.push({ label: "Subtotal:", value: fmt(subtotal) });
    if (hasDiscount) {
      const discountLabel = estimateDiscountType === "percent"
        ? `Discount (${estimateDiscountValue}%):`
        : "Discount:";
      totalsRows.push({ label: discountLabel, value: `-${fmt(discountAmount)}` });
    }
    if (estimateTaxEnabled) {
      if (activeTaxLines.length > 0) {
        for (const tl of activeTaxLines) {
          const tlLabel = tl.taxId ? `${tl.name} [${tl.taxId}] (${tl.rate}%):` : `${tl.name} (${tl.rate}%):`;
          totalsRows.push({ label: tlLabel, value: fmt(taxableBase * (tl.rate / 100)) });
        }
      } else {
        totalsRows.push({ label: `Tax (${estimateTaxRate}%):`, value: fmt(taxAmount) });
      }
    }
  }
  totalsRows.push({ label: `Grand Total (${currencyLabel}):`, value: fmt(grandTotal), bold: true, separator: showBreakdown });
  if (estimateDepositValue > 0 && !hasMilestones) {
    const depositLabel = estimateDepositType === "percent"
      ? `Deposit Requested (${estimateDepositValue}%) — ${fmt(depositAmount)}`
      : "Deposit Requested";
    totalsRows.push({ label: depositLabel, value: estimateDepositType === "percent" ? "" : fmt(depositAmount) });
  }

  totalsRows.forEach((row, i) => {
    const ry = ty + i * totalsRowH;
    if (row.separator) {
      doc.setDrawColor(accentRgb[0], accentRgb[1], accentRgb[2]);
      doc.setLineWidth(0.3);
      doc.line(totalsX, ry - 3.5, rightX, ry - 3.5);
    }
    doc.setFont("helvetica", row.bold ? "bold" : "normal");
    doc.setFontSize(row.bold ? 9.5 : 9);
    doc.setTextColor(30, 30, 30);
    doc.text(row.label, rightX - 36, ry, { align: "right" });
    doc.setFont("helvetica", row.bold ? "bold" : "normal");
    doc.text(row.value, rightX, ry, { align: "right" });
  });

  ty += totalsRows.length * totalsRowH + 8;

  if (hasMilestones) {
    ty = renderMilestonesSection(doc, ty, margin, rightX, pageHeight, estimate.milestones!, grandTotal, accentRgb, fmt);
  } else if (estimate.isRecurring) {
    ty = renderRecurringSection(doc, ty, margin, rightX, pageHeight, estimate, grandTotal, accentRgb, fmt, fmtDate);
  }

  const estimateTerms = estimate.termsAndNotes?.trim() || company.termsAndNotes?.trim() || "";
  if (estimateTerms) {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8.5);
    const lines: string[] = doc.splitTextToSize(estimateTerms, rightX - margin);
    const headerHeight = 11;
    const lineHeight = 4.2;
    const estimatedHeight = headerHeight + lines.length * lineHeight;
    if (ty + estimatedHeight > pageHeight - FOOTER_RESERVE) {
      doc.addPage();
      ty = margin;
    }
    doc.setDrawColor(200, 200, 200);
    doc.setLineWidth(0.3);
    doc.line(margin, ty, rightX, ty);
    ty += 6;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.setTextColor(30, 30, 30);
    doc.text("Notes / Terms", margin, ty);
    ty += 5;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8.5);
    doc.setTextColor(60, 60, 60);
    let lineY = ty;
    for (const line of lines) {
      if (lineY > pageHeight - FOOTER_RESERVE) {
        doc.addPage();
        lineY = margin;
      }
      doc.text(line, margin, lineY);
      lineY += lineHeight;
    }
  }

  const totalPages = doc.getNumberOfPages();
  const footerText = company.pdfFooter?.trim() ?? "Created with QuoTrak — Professional quoting & invoicing made simple.\nhttps://quotrak.com";
  const footerY = pageHeight - 8;
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7.5);
    doc.setTextColor(150, 150, 150);
    doc.setDrawColor(210, 210, 210);
    doc.setLineWidth(0.3);
    doc.line(margin, footerY - 3.5, rightX, footerY - 3.5);
    if (footerText) {
      doc.text(footerText, margin, footerY, { align: "left" });
    }
    doc.text(`Page ${i} of ${totalPages}`, rightX, footerY, { align: "right" });
  }

  return doc;
}

export function buildPdfFilename(estimate: EstimateData, prefix?: string): string {
  const num = estimate.estimateNumber ? `QUO-${estimate.estimateNumber}` : `QUO-${estimate.id}`;
  const customer = (estimate.customerName || "Unknown").replace(/\s+/g, "-").replace(/[^a-zA-Z0-9-]/g, "");
  const dateStr = estimate.estimateDate
    ? estimate.estimateDate.slice(0, 10)
    : estimate.createdAt.slice(0, 10);
  const base = `${num}-${customer}-${dateStr}.pdf`;
  return prefix ? `${prefix}-${base}` : base;
}

export function exportCustomerPDF(estimate: EstimateData, company: CompanySettings = {}) {
  const doc = buildCustomerPDFDoc(estimate, company);
  doc.save(buildPdfFilename(estimate));
}

export function previewCustomerPDF(estimate: EstimateData, company: CompanySettings = {}): string {
  const doc = buildCustomerPDFDoc(estimate, company);
  return doc.output("bloburl") as unknown as string;
}

export function customerPDFBase64(estimate: EstimateData, company: CompanySettings = {}): string {
  const doc = buildCustomerPDFDoc(estimate, company);
  const dataUri = doc.output("datauristring");
  return dataUri.split(",")[1] ?? "";
}

export function customerPDFFilename(estimate: EstimateData): string {
  const num = estimate.estimateNumber ? estimate.estimateNumber.trim() : `EST-${estimate.id}`;
  return `${num}.pdf`;
}

function buildInternalPDFDoc(estimate: EstimateData, company: CompanySettings = {}): jsPDF {
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "letter" });
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 18;
  const fmtDate = (dateStr: string) => formatDateWithTimezone(dateStr, company.timezone);

  const accentRgb = hexToRgb(company.accentColor || "#1e3a5f");

  const hasCompany = !!(company.companyName || company.phone || company.email || company.address || company.addressLine1 || company.city || company.state || company.postalCode || company.country);
  const rightX = pageWidth - margin;

  // --- Minimal header: no colored background ---
  let y = 12;
  let logoEndX = margin;
  if (company.logoBase64) {
    try {
      const logoH = 16;
      doc.addImage(company.logoBase64, "PNG", margin, y - 7, 0, logoH);
      logoEndX = margin + logoH * 1.6 + 3;
    } catch {
      logoEndX = margin;
    }
  }

  if (hasCompany) {
    if (company.companyName) {
      doc.setFont("helvetica", "bold");
      doc.setFontSize(11);
      doc.setTextColor(30, 30, 30);
      doc.text(company.companyName, logoEndX, y);
      y += 5;
    }
    const contactParts: string[] = [];
    if (company.phone) contactParts.push(company.phone);
    if (company.email) contactParts.push(company.email);
    if (company.website) contactParts.push(company.website);
    const addrLines = buildAddressLines(company);
    if (addrLines.length > 0) contactParts.push(addrLines.join(", "));
    if (contactParts.length > 0) {
      doc.setFont("helvetica", "normal");
      doc.setFontSize(7.5);
      doc.setTextColor(100, 100, 100);
      doc.text(contactParts.join("  |  "), logoEndX, y, { maxWidth: pageWidth - logoEndX - 70 });
      y += 4;
    }
  }

  // Right side: COST REPORT label (accent, bold) + estimate title + info row
  doc.setFont("helvetica", "bold");
  doc.setFontSize(13);
  doc.setTextColor(accentRgb[0], accentRgb[1], accentRgb[2]);
  doc.text("COST REPORT", rightX, 12, { align: "right" });

  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.setTextColor(30, 30, 30);
  doc.text(estimate.title, rightX, 19, { align: "right" });

  const infoItems: string[] = [];
  if (estimate.customerName) infoItems.push(`Client: ${estimate.customerName}`);
  infoItems.push(`Date: ${estimate.estimateDate ? fmtDate(estimate.estimateDate) : fmtDate(estimate.createdAt)}`);
  infoItems.push(`Status: ${estimate.status.charAt(0).toUpperCase() + estimate.status.slice(1)}`);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7.5);
  doc.setTextColor(80, 80, 80);
  doc.text(infoItems.join("   |   "), rightX, 25, { align: "right" });

  // Divider below the two-column header strip
  const headerBottomY = Math.max(y, 28) + 2;
  doc.setDrawColor(220, 220, 220);
  doc.line(margin, headerBottomY, rightX, headerBottomY);
  y = headerBottomY + 5;

  if (estimate.description) {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(60, 60, 60);
    const descLines = doc.splitTextToSize(estimate.description, pageWidth - 36);
    doc.text(descLines, margin, y);
    y += descLines.length * 4.5;
    doc.setDrawColor(220, 220, 220);
    doc.line(margin, y + 2, rightX, y + 2);
    y += 6;
  }

  type FlatCost = {
    name: string;
    quantity: number;
    unit: string | null;
    unitCost: number;
    totalCost: number;
    type: string;
    category: string;
  };

  const TYPE_ORDER = ["labour", "materials", "fees"];
  const TYPE_LABELS: Record<string, string> = {
    labour: "Labor",
    materials: "Materials",
    fees: "Fees",
  };

  const allCosts: FlatCost[] = [];
  for (const li of estimate.lineItems) {
    if (li.materials && li.materials.length > 0) {
      for (const mat of li.materials) {
        allCosts.push({
          name: mat.name || "Unnamed",
          quantity: mat.quantity,
          unit: mat.unit ?? null,
          unitCost: mat.unitCost,
          totalCost: mat.totalCost,
          type: (mat.type ?? "materials").trim().toLowerCase(),
          category: mat.category?.trim() || "Uncategorized",
        });
      }
    }
  }

  const costsByType = new Map<string, FlatCost[]>();
  for (const cost of allCosts) {
    const key = cost.type;
    if (!costsByType.has(key)) costsByType.set(key, []);
    costsByType.get(key)!.push(cost);
  }

  const orderedTypes = [
    ...TYPE_ORDER.filter(t => costsByType.has(t)),
    ...[...costsByType.keys()].filter(t => !TYPE_ORDER.includes(t)),
  ];

  if (orderedTypes.length === 0) {
    doc.setFont("helvetica", "italic");
    doc.setFontSize(9);
    doc.setTextColor(120, 120, 120);
    doc.text("No internal cost entries recorded.", margin, y + 4);
    y += 12;
  } else {
    for (const typeKey of orderedTypes) {
      const typeCosts = costsByType.get(typeKey)!;
      const typeLabel = TYPE_LABELS[typeKey] ?? (typeKey.charAt(0).toUpperCase() + typeKey.slice(1));
      const typeTotal = typeCosts.reduce((s, c) => s + c.totalCost, 0);

      const categoryMap = new Map<string, FlatCost[]>();
      for (const cost of typeCosts) {
        if (!categoryMap.has(cost.category)) categoryMap.set(cost.category, []);
        categoryMap.get(cost.category)!.push(cost);
      }

      type BodyRowMeta = { kind: "category-header" | "cost" | "category-subtotal" };
      const tableBody: string[][] = [];
      const rowMeta: BodyRowMeta[] = [];

      for (const [catName, catCosts] of categoryMap) {
        const catTotal = catCosts.reduce((s, c) => s + c.totalCost, 0);
        tableBody.push([`  ${catName}`, "", "", "", ""]);
        rowMeta.push({ kind: "category-header" });
        for (const cost of catCosts) {
          tableBody.push([
            cost.name,
            cost.quantity % 1 === 0 ? cost.quantity.toString() : cost.quantity.toFixed(2),
            cost.unit ?? "—",
            formatCurrencyWithLabel(cost.unitCost, company.currencyLabel),
            formatCurrencyWithLabel(cost.totalCost, company.currencyLabel),
          ]);
          rowMeta.push({ kind: "cost" });
        }
        tableBody.push(["", "", "", "Subtotal", formatCurrencyWithLabel(catTotal, company.currencyLabel)]);
        rowMeta.push({ kind: "category-subtotal" });
      }

      if (y > doc.internal.pageSize.getHeight() - 60) {
        doc.addPage();
        y = margin;
      }

      // Type section label (accent color, bold)
      doc.setFont("helvetica", "bold");
      doc.setFontSize(10);
      doc.setTextColor(accentRgb[0], accentRgb[1], accentRgb[2]);
      doc.text(typeLabel, margin, y);
      y += 4;

      autoTable(doc, {
        startY: y,
        head: [["Name", "Qty", "Unit", "Unit Cost", "Total"]],
        body: tableBody,
        styles: { fontSize: 8.5, cellPadding: 2, textColor: [30, 30, 30] },
        headStyles: {
          fillColor: false,
          textColor: [80, 80, 80],
          fontStyle: "bold",
          fontSize: 8,
          lineWidth: { bottom: 0.4 },
          lineColor: [180, 180, 180],
        },
        columnStyles: {
          0: { cellWidth: "auto" },
          1: { cellWidth: 18, halign: "center" },
          2: { cellWidth: 22, halign: "center" },
          3: { cellWidth: 32, halign: "right" },
          4: { cellWidth: 32, halign: "right", fontStyle: "bold" },
        },
        margin: { left: margin, right: margin, bottom: 22 },
        theme: "plain",
        didParseCell: (data: CellHookData) => {
          if (data.section === "head") {
            data.cell.styles.lineColor = [180, 180, 180];
            data.cell.styles.lineWidth = { bottom: 0.4 };
            return;
          }
          if (data.section !== "body") return;
          const meta = rowMeta[data.row.index];
          if (!meta) return;
          if (meta.kind === "category-header") {
            data.cell.styles.fontStyle = "bold";
            data.cell.styles.textColor = [accentRgb[0], accentRgb[1], accentRgb[2]];
            data.cell.styles.fontSize = 8;
          } else if (meta.kind === "category-subtotal") {
            data.cell.styles.fontStyle = "bold";
            data.cell.styles.textColor = [80, 80, 80];
          }
          data.cell.styles.lineColor = [220, 220, 220];
          data.cell.styles.lineWidth = { bottom: 0.2 };
        },
      });

      let tableEndY = (doc as any).lastAutoTable.finalY;

      // Plain right-aligned type total line (no filled footer row)
      // Guard against collision with confidential footer (footer renders at pageHeight - 18;
      // safeBottom uses -22 to leave a clear gap above it)
      const safeBottom = doc.internal.pageSize.getHeight() - 22;
      if (tableEndY + 8 > safeBottom) {
        doc.addPage();
        tableEndY = margin;
      }
      doc.setFont("helvetica", "bold");
      doc.setFontSize(8.5);
      doc.setTextColor(accentRgb[0], accentRgb[1], accentRgb[2]);
      doc.text(
        `${typeLabel} Total: ${formatCurrencyWithLabel(typeTotal, company.currencyLabel)}`,
        rightX,
        tableEndY + 5,
        { align: "right" },
      );

      y = tableEndY + 10;
    }
  }

  // --- Compact inline margin summary with top divider ---
  const grandInternalCostTotal = allCosts.reduce((s, c) => s + c.totalCost, 0);
  const marginValue = estimate.margin || 0;

  if (y > doc.internal.pageSize.getHeight() - 30) {
    doc.addPage();
    y = margin;
  }

  doc.setDrawColor(180, 180, 180);
  doc.line(margin, y, rightX, y);
  y += 5;

  // Profit/margin use revenueBase = subtotal − discount (pre-tax, post-discount)
  // from the server. If `profit` is missing (e.g. stale/manual payloads),
  // recompute from subtotal − discount − internal cost rather than
  // total − internal cost so the fallback never silently drifts back to the
  // old tax-including formula.
  const fallbackRevenueBase = (estimate.subtotal ?? estimate.total) - (estimate.discountAmount ?? 0);
  const profitValue = estimate.profit ?? (fallbackRevenueBase - grandInternalCostTotal);
  const colW = (pageWidth - margin * 2) / 4;
  const summaryItems = [
    { label: "Customer Total", value: formatCurrencyWithLabel(estimate.total, company.currencyLabel), color: null as [number,number,number] | null },
    { label: "Internal Cost", value: formatCurrencyWithLabel(grandInternalCostTotal, company.currencyLabel), color: null as [number,number,number] | null },
    {
      label: "Profit",
      value: formatCurrencyWithLabel(profitValue, company.currencyLabel),
      color: profitValue < 0 ? [200, 0, 0] as [number,number,number] : [0, 140, 70] as [number,number,number],
    },
    {
      label: "Estimated Margin",
      value: `${marginValue.toFixed(1)}%`,
      color: marginValue < 0 ? [200, 0, 0] as [number,number,number] : [0, 140, 70] as [number,number,number],
    },
  ];

  summaryItems.forEach((item, i) => {
    const x = margin + colW * i + colW / 2;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(100, 100, 100);
    doc.text(item.label, x, y, { align: "center" });
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    if (item.color) {
      doc.setTextColor(item.color[0], item.color[1], item.color[2]);
    } else {
      doc.setTextColor(30, 30, 30);
    }
    doc.text(item.value, x, y + 5, { align: "center" });
  });

  const totalPages = doc.getNumberOfPages();
  const footerY = doc.internal.pageSize.getHeight() - 18;
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.setTextColor(180, 50, 50);
    doc.text("CONFIDENTIAL — Internal use only. Do not share with client.", pageWidth / 2, footerY, { align: "center" });
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7.5);
    doc.setTextColor(100, 100, 100);
    doc.text(`Page ${i} of ${totalPages}`, rightX, footerY, { align: "right" });
  }

  return doc;
}

export function exportInternalPDF(estimate: EstimateData, company: CompanySettings = {}) {
  const doc = buildInternalPDFDoc(estimate, company);
  doc.save(buildPdfFilename(estimate, "INTERNAL"));
}

export function previewInternalPDF(estimate: EstimateData, company: CompanySettings = {}): string {
  const doc = buildInternalPDFDoc(estimate, company);
  return doc.output("bloburl") as unknown as string;
}

export type InvoiceData = {
  id: number;
  invoiceNumber?: string | null;
  title: string;
  status: string;
  customerName?: string | null;
  customerPhone?: string | null;
  customerEmail?: string | null;
  customerAddressLine1?: string | null;
  customerAddressLine2?: string | null;
  customerCity?: string | null;
  customerState?: string | null;
  customerPostalCode?: string | null;
  customerCountry?: string | null;
  description?: string | null;
  total: number;
  subtotal?: number;
  taxAmount?: number;
  taxEnabled?: boolean;
  taxRate?: number;
  selectedTaxLines?: string | null;
  internalCost?: number;
  margin?: number;
  profit?: number;
  depositType?: "fixed" | "percent" | null;
  depositValue?: number | null;
  discountType?: "fixed" | "percent" | null;
  discountValue?: number | null;
  discountAmount?: number;
  depositChildId?: number | null;
  depositChildStatus?: string | null;
  depositChildPaidDate?: string | null;
  isDepositChild?: boolean | null;
  depositParentId?: number | null;
  isRecurring?: boolean | null;
  recurringInterval?: string | null;
  recurringCustomDays?: number | null;
  recurringStartDate?: string | null;
  recurringEndDate?: string | null;
  invoiceDate?: string | null;
  dueDate?: string | null;
  stripePaymentLinkUrl?: string | null;
  acceptOnlinePayment?: boolean;
  createdAt: string;
  isMilestoneParent?: boolean | null;
  termsAndNotes?: string | null;
  milestones?: Array<{
    name: string;
    percentage?: number | null;
    amount?: number | null;
    status?: string | null;
  }>;
  payments?: Array<{
    id: number;
    amount: number;
    paymentDate: string;
    notes?: string | null;
    source?: string | null;
  }>;
  lineItems: Array<{
    description: string;
    productName?: string | null;
    quantity: number;
    unitPrice: number;
    total: number;
    privateNote?: string | null;
    internalCostTotal?: number;
    groupId?: number | null;
    materials?: Array<{
      id: number;
      name: string;
      quantity: number;
      unit?: string | null;
      unitCost: number;
      totalCost: number;
    }>;
  }>;
  groups?: Array<{ id: number; name: string; sortOrder: number }>;
};

export async function buildInvoicePDFDoc(invoice: InvoiceData, company: CompanySettings = {}): Promise<jsPDF> {
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "letter" });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 18;

  const accentRgb = hexToRgb(company.invoiceAccentColor || company.accentColor || "#1e3a5f");
  const currencyLabel = company.invoiceCurrencyLabel?.trim() || company.currencyLabel || "USD";
  const colConfig = parseColumnConfig(company.invoiceColumnConfig || company.columnConfig);

  const depositType = invoice.depositType ?? "percent";
  const depositValue = invoice.depositValue ?? 0;
  const taxEnabled = invoice.taxEnabled ?? false;
  const taxRate = invoice.taxRate ?? 0;
  const selectedTaxLines = reconcileTaxLineIds(parseTaxLines(invoice.selectedTaxLines), company.taxLines);

  const fmt = (v: number) => formatCurrencyWithLabel(v, currencyLabel);
  const fmtDate = (dateStr: string) => formatDateWithTimezone(dateStr, company.timezone);

  let y = margin;
  const rightX = pageWidth - margin;

  const maxLogoW = 50;
  const maxLogoH = 28;
  let logoW = maxLogoW;
  let logoH = maxLogoH;

  if (company.logoBase64) {
    try {
      const props = doc.getImageProperties(company.logoBase64);
      const scale = Math.min(maxLogoW / props.width, maxLogoH / props.height);
      logoW = props.width * scale;
      logoH = props.height * scale;
      doc.addImage(company.logoBase64, "PNG", margin, y, logoW, logoH);
    } catch {}
  }

  const invoiceDocTitle = company.invoiceDocumentTitle?.trim() || "INVOICE";
  doc.setFont("helvetica", "normal");
  doc.setFontSize(36);
  doc.setTextColor(30, 30, 30);
  doc.text(invoiceDocTitle, rightX, y + 10, { align: "right" });

  const companyInfoY = y + 16;
  const companyLines: string[] = [];
  if (company.companyName) companyLines.push(company.companyName);
  buildAddressLines(company).forEach(l => companyLines.push(l));
  if (company.phone) companyLines.push(company.phone);
  if (company.website) companyLines.push(company.website);
  if (company.email) companyLines.push(company.email);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(8.5);
  doc.setTextColor(50, 50, 50);
  companyLines.forEach((line, i) => {
    if (i === 0) {
      doc.setFont("helvetica", "bold");
      doc.setFontSize(10);
    } else {
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8.5);
    }
    doc.text(line, rightX, companyInfoY + i * 4.8, { align: "right" });
  });

  y = Math.max(y + logoH + 6, companyInfoY + companyLines.length * 4.8 + 4);

  doc.setDrawColor(180, 180, 180);
  doc.setLineWidth(0.4);
  doc.line(margin, y, rightX, y);
  y += 8;

  const billToX = margin;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7.5);
  doc.setTextColor(120, 120, 120);
  doc.text("BILL TO", billToX, y);
  y += 4;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.setTextColor(20, 20, 20);
  doc.text(invoice.customerName || "—", billToX, y);
  y += 5;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(60, 60, 60);
  if (invoice.customerPhone) { doc.text(invoice.customerPhone, billToX, y); y += 5; }
  if (invoice.customerEmail) { doc.text(invoice.customerEmail, billToX, y); y += 5; }
  if (invoice.customerAddressLine1) { doc.text(invoice.customerAddressLine1, billToX, y); y += 5; }
  if (invoice.customerAddressLine2) { doc.text(invoice.customerAddressLine2, billToX, y); y += 5; }
  const invCityState = [invoice.customerCity, invoice.customerState].filter(Boolean).join(", ");
  const invPostalCountry = [invoice.customerPostalCode, invoice.customerCountry].filter(Boolean).join(" ");
  if (invCityState) { doc.text(invCityState, billToX, y); y += 5; }
  if (invPostalCountry) { doc.text(invPostalCountry, billToX, y); y += 5; }

  const metaY = y - ((invoice.customerPhone ? 5 : 0) + (invoice.customerEmail ? 5 : 0) +
    (invoice.customerAddressLine1 ? 5 : 0) + (invoice.customerAddressLine2 ? 5 : 0) +
    (invCityState ? 5 : 0) + (invPostalCountry ? 5 : 0) + 9);
  const metaRows: Array<[string, string]> = [];
  if (invoice.invoiceNumber) metaRows.push(["Invoice #", invoice.invoiceNumber]);
  if (invoice.invoiceDate) metaRows.push(["Invoice Date", fmtDate(invoice.invoiceDate)]);
  if (invoice.dueDate) metaRows.push(["Due Date", fmtDate(invoice.dueDate)]);

  metaRows.forEach(([label, value], i) => {
    const rowY = metaY + i * 7;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8.5);
    doc.setTextColor(30, 30, 30);
    doc.text(label + ":", rightX - 40, rowY, { align: "right" });
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8.5);
    doc.setTextColor(30, 30, 30);
    doc.text(value, rightX, rowY, { align: "right" });
  });

  y += 6;

  if (invoice.description) {
    doc.setFont("helvetica", "italic");
    doc.setFontSize(8.5);
    doc.setTextColor(80, 80, 80);
    const descLines = doc.splitTextToSize(invoice.description, pageWidth - margin * 2);
    doc.text(descLines, margin, y);
    y += descLines.length * 4.5 + 4;
  }

  y += 2;

  const visibleCols = Object.entries(colConfig).filter(([, cfg]) => cfg.visible);
  const colCount = visibleCols.length;
  const amountWidth = 28;
  const descWidth = pageWidth - margin * 2 - (colCount - 1) * 22 - amountWidth;

  const head = visibleCols.map(([, cfg]) => cfg.label);
  const invoiceDescColIdx = visibleCols.findIndex(([k]) => k === "description");

  const invSortedGroups = [...(invoice.groups ?? [])].sort((a, b) => a.sortOrder - b.sortOrder);
  const invGroupMap = new Map(invSortedGroups.map(g => [g.id, g]));

  type InvBodyRow = string[] | Array<{ content: string; colSpan?: number; styles?: object }>;
  type InvRowMeta = { kind: "item"; item: typeof invoice.lineItems[0] } | { kind: "group"; name: string };
  const body: InvBodyRow[] = [];
  const invRowMeta: InvRowMeta[] = [];
  let invActiveGroupName: string | null = null;
  let invActiveGroupPage: number = 0;

  const buildInvItemRow = (item: typeof invoice.lineItems[0]): string[] =>
    visibleCols.map(([key]) => {
      if (key === "description") return item.productName ? "" : item.description;
      if (key === "qty") return String(item.quantity);
      if (key === "unitPrice") return fmt(item.unitPrice);
      if (key === "amount") return fmt(item.total);
      return "";
    });

  const invUngrouped = invoice.lineItems.filter(li => !li.groupId || !invGroupMap.has(li.groupId as number));
  for (const item of invUngrouped) {
    body.push(buildInvItemRow(item));
    invRowMeta.push({ kind: "item", item });
  }

  const invAmountColIdx = visibleCols.findIndex(([k]) => k === "amount");
  for (const group of invSortedGroups) {
    const groupItems = invoice.lineItems.filter(li => li.groupId === group.id);
    if (groupItems.length === 0) continue;
    const invGroupTotal = groupItems.reduce((s, item) => s + item.total, 0);
    const invGroupHeaderStyles = { fontStyle: "bold" as const, fillColor: [220, 225, 235] as [number, number, number], textColor: [30, 30, 30] as [number, number, number], cellPadding: { top: 4, bottom: 4, left: 6, right: 4 } };
    const invGroupHeaderRow = invAmountColIdx >= 0
      ? [
          { content: group.name, colSpan: colCount - 1, styles: invGroupHeaderStyles },
          { content: fmt(invGroupTotal), styles: { ...invGroupHeaderStyles, textColor: [100, 100, 100] as [number, number, number], halign: "right" as const, cellPadding: { top: 4, bottom: 4, left: 4, right: 4 } } },
        ]
      : [{ content: group.name, colSpan: colCount, styles: invGroupHeaderStyles }];
    body.push(invGroupHeaderRow);
    invRowMeta.push({ kind: "group", name: group.name });
    for (const item of groupItems) {
      body.push(buildInvItemRow(item));
      invRowMeta.push({ kind: "item", item });
    }
  }

  autoTable(doc, {
    startY: y,
    head: [head],
    body,
    margin: { left: margin, right: margin, bottom: FOOTER_RESERVE },
    // See buildCustomerPDFDoc: manually drawn product rows must not split.
    rowPageBreak: "avoid",
    styles: { fontSize: 9.5, cellPadding: { top: 4, bottom: 4, left: 4, right: 4 }, overflow: "linebreak", textColor: [20, 20, 20], lineColor: [220, 220, 220], lineWidth: 0 },
    headStyles: {
      fillColor: accentRgb,
      textColor: [255, 255, 255],
      fontStyle: "bold",
      fontSize: 9.5,
      cellPadding: { top: 2, right: 4, bottom: 2, left: 4 },
    },
    columnStyles: {
      [visibleCols.findIndex(([k]) => k === "amount")]: { halign: "right", fontStyle: "bold", cellWidth: amountWidth },
      [visibleCols.findIndex(([k]) => k === "qty")]: { halign: "center", cellWidth: 18 },
      [visibleCols.findIndex(([k]) => k === "unitPrice")]: { halign: "right", cellWidth: 28 },
    },
    theme: "plain",
    tableLineWidth: 0,
    didParseCell: (data: CellHookData) => {
      if (data.section === "body") {
        const meta = invRowMeta[data.row.index];
        if (meta?.kind === "group") {
          data.cell.styles.lineWidth = { top: 0, bottom: 0, left: 0, right: 0 };
          keepRowWithNext(data, doc, FOOTER_RESERVE);
          return;
        }
        data.cell.styles.lineWidth = { top: 0, bottom: 0.2, left: 0, right: 0 };
        data.cell.styles.lineColor = [220, 220, 220];
        if (invoiceDescColIdx >= 0 && data.column.index === invoiceDescColIdx && meta?.kind === "item") {
          const item = meta.item;
          if (item?.productName) {
            const pad = 4;
            const pw = doc.internal.pageSize.getWidth();
            const fixedWidths = [amountWidth, 18, 28].reduce((sum, w) => sum + w, 0);
            const cellW = pw - margin * 2 - fixedWidths;
            const textW = cellW - pad * 2;
            doc.setFont("helvetica", "bold");
            doc.setFontSize(9.5);
            const nameLines = doc.splitTextToSize(item.productName, textW);
            doc.setFont("helvetica", "normal");
            doc.setFontSize(9.5);
            const descLines = doc.splitTextToSize(item.description, textW);
            const lineHeight = 5;
            const contentHeight = pad + 2.5 + (nameLines.length + descLines.length) * lineHeight + pad;
            data.cell.styles.minCellHeight = contentHeight;
          }
        }
      }
      if (data.section === "head") {
        data.cell.styles.lineWidth = 0;
      }
    },
    didDrawCell: (data: CellHookData) => {
      if (data.section === "body") {
        const meta = invRowMeta[data.row.index];
        if (meta?.kind === "group") { invActiveGroupName = meta.name; invActiveGroupPage = data.pageNumber; }
        if (meta?.kind === "item" && data.column.index === colCount - 1) {
          const nextMeta = invRowMeta[data.row.index + 1];
          if (!nextMeta || nextMeta.kind === "group") invActiveGroupName = null;
        }
        if (invoiceDescColIdx >= 0 && data.column.index === invoiceDescColIdx) {
          if (meta?.kind !== "item") return;
          const item = meta.item;
          if (item?.productName) {
            const { x, y: cy, width, height } = data.cell;
            const pad = 4;
            const fillW = width - 1;
            const fillH = height - 1;
            doc.setFillColor(255, 255, 255);
            doc.rect(x + 0.5, cy + 0.5, fillW, fillH, "F");
            doc.setFont("helvetica", "bold");
            doc.setFontSize(9.5);
            doc.setTextColor(20, 20, 20);
            const nameLines = doc.splitTextToSize(item.productName, width - pad * 2);
            const lineHeight = 5;
            nameLines.forEach((line: string, i: number) => {
              doc.text(line, x + pad, cy + pad + 2.5 + i * lineHeight);
            });
            doc.setFont("helvetica", "normal");
            doc.setFontSize(9.5);
            doc.setTextColor(20, 20, 20);
            const descLines = doc.splitTextToSize(item.description, width - pad * 2);
            descLines.forEach((line: string, i: number) => {
              doc.text(line, x + pad, cy + pad + 2.5 + nameLines.length * lineHeight + i * lineHeight);
            });
          }
        }
      }
    },
    didDrawPage: (data: any) => {
      if (data.pageNumber > 1 && invActiveGroupName !== null && data.cursor !== null && data.pageNumber !== invActiveGroupPage) {
        const cur = data.cursor as { x: number; y: number };
        const pw = doc.internal.pageSize.getWidth();
        const headingH = 9;
        doc.setFillColor(220, 225, 235);
        doc.rect(margin, cur.y, pw - margin * 2, headingH, "F");
        doc.setFont("helvetica", "bold");
        doc.setFontSize(9.5);
        doc.setTextColor(30, 30, 30);
        doc.text(`${invActiveGroupName} (continued)`, margin + 6, cur.y + 6);
        doc.setFont("helvetica", "normal");
        doc.setTextColor(20, 20, 20);
        cur.y += headingH + 1;
      }
    },
  });

  const finalY = (doc as any).lastAutoTable?.finalY ?? y + 20;
  let summaryY = finalY + 8;

  const summaryRowsCount =
    1 +
    (taxEnabled ? (selectedTaxLines.length > 0 ? selectedTaxLines.length : (taxRate > 0 ? 1 : 0)) : 0) +
    1 +
    (depositValue > 0 && (invoice.milestones?.length ?? 0) === 0 ? 2 : 0);
  if (summaryY + summaryRowsCount * 7 + 8 > pageHeight - FOOTER_RESERVE) {
    doc.addPage();
    summaryY = margin;
  }

  const subtotal = invoice.subtotal ?? invoice.lineItems.reduce((s, i) => s + i.total, 0);
  const invoiceDiscountType = invoice.discountType ?? null;
  const invoiceDiscountValue = invoice.discountValue ?? null;
  const { discountAmount, taxableBase: discountedBase, taxAmount: computedTaxAmount } = computeDocumentTotals({
    subtotal,
    discountType: invoiceDiscountType,
    discountValue: invoiceDiscountValue,
    taxEnabled,
    taxRate,
    selectedTaxLines: invoice.selectedTaxLines,
  });
  const taxAmountCalc = invoice.taxAmount ?? computedTaxAmount;
  const total = invoice.total ?? (discountedBase + taxAmountCalc);
  const depositAmount = depositType === "percent" ? (total * depositValue / 100) : depositValue;

  const summaryRows: Array<[string, string]> = [["Subtotal", fmt(subtotal)]];
  if (discountAmount > 0) {
    const discountLabel = invoiceDiscountType === "percent"
      ? `Discount (${invoiceDiscountValue}%)`
      : "Discount";
    summaryRows.push([discountLabel, `-${fmt(discountAmount)}`]);
  }
  if (taxEnabled) {
    if (selectedTaxLines.length > 0) {
      selectedTaxLines.forEach(tl => {
        const tlLabel = tl.taxId ? `${tl.name} [${tl.taxId}] (${tl.rate}%)` : `${tl.name} (${tl.rate}%)`;
        summaryRows.push([tlLabel, fmt(discountedBase * tl.rate / 100)]);
      });
    } else if (taxRate > 0) {
      summaryRows.push([`Tax (${taxRate}%)`, fmt(taxAmountCalc)]);
    }
  }
  summaryRows.push(["Total", fmt(total)]);
  const hasMilestones = (invoice.milestones?.length ?? 0) > 0;
  const depositPaid = invoice.depositChildStatus === "paid";
  if (depositValue > 0 && !hasMilestones) {
    if (depositPaid) {
      const dateLabel = invoice.depositChildPaidDate ? ` (${fmtDate(invoice.depositChildPaidDate)})` : "";
      summaryRows.push([`Deposit Paid${dateLabel}`, `-${fmt(depositAmount)}`]);
    } else {
      summaryRows.push([`Deposit (${depositType === "percent" ? `${depositValue}%` : "fixed"})`, fmt(depositAmount)]);
      summaryRows.push(["Balance Due", fmt(total - depositAmount)]);
    }
  }

  const allPayments = invoice.payments ?? [];
  // The "Deposit Paid" summary row already represents the deposit-sourced
  // payment that was rolled up from the deposit child. Suppress it from the
  // per-payment list so the parent PDF doesn't double-subtract.
  // When depositPaid, the "Deposit Paid" summary row already accounts for the
  // deposit payment, so suppress it from the per-payment list to avoid double-
  // subtracting. When not depositPaid there is no separate deposit row, so
  // include all recorded payments.
  const payments = depositPaid
    ? allPayments.filter(p => p.source !== "deposit")
    : allPayments;
  const totalPaid = allPayments.reduce((s, p) => s + p.amount, 0);
  const hasPayments = payments.length > 0;
  if (hasPayments) {
    payments.forEach((p, idx) => {
      const label = `Payment ${idx + 1}${p.paymentDate ? ` (${p.paymentDate})` : ""}`;
      summaryRows.push([label, `-${fmt(p.amount)}`]);
    });
  }
  if (hasPayments || depositPaid) {
    // When a deposit is set but not yet paid via the formal deposit child path,
    // "Balance Due" already shows (total - depositAmount). Additional manual
    // payments reduce that balance, not the full total — so we compute
    // Balance Owing as max(0, (total - depositAmount) - nonDepositPayments).
    // This prevents the mixed-state double-subtraction bug.
    const nonDepositPaymentTotal = payments.reduce((s, p) => s + p.amount, 0);
    const balanceOwing = depositValue > 0 && !hasMilestones && !depositPaid
      ? Math.max(0, (total - depositAmount) - nonDepositPaymentTotal)
      : Math.max(0, total - totalPaid);
    summaryRows.push(["Balance Owing", fmt(balanceOwing)]);
  }

  const summaryLabelX = pageWidth - margin - 70;
  const summaryValueX = rightX;
  const totalsRowH = 7;

  summaryRows.forEach(([label, value], i) => {
    const isTotal = label === "Total";
    const isBalance = label === "Balance Due" || label === "Balance Owing";
    const isPayment = label.startsWith("Payment ") && label !== "Payment Date";
    const isBold = isTotal || isBalance;
    const rowY = summaryY + i * totalsRowH;

    if (isBold) {
      doc.setFillColor(240, 240, 240);
      doc.rect(summaryLabelX - 4, rowY - 4.5, summaryValueX - summaryLabelX + 8, totalsRowH, "F");
    }

    doc.setFont("helvetica", isBold ? "bold" : "normal");
    doc.setFontSize(isBold ? 9.5 : 9);
    doc.setTextColor(30, 30, 30);

    doc.text(label, summaryValueX - 36, rowY, { align: "right" });
    doc.setFont("helvetica", isBold ? "bold" : "normal");
    doc.text(value, summaryValueX, rowY, { align: "right" });
    doc.setTextColor(30, 30, 30);
  });

  let extraContentY = summaryY + summaryRows.length * totalsRowH + 8;

  if (hasMilestones) {
    extraContentY = renderMilestonesSection(doc, extraContentY, margin, rightX, pageHeight, invoice.milestones!, total, accentRgb, fmt);
  } else if (invoice.isRecurring) {
    extraContentY = renderRecurringSection(doc, extraContentY, margin, rightX, pageHeight, invoice, total, accentRgb, fmt, fmtDate);
  }

  const showPaymentLink = invoice.acceptOnlinePayment !== false && invoice.stripePaymentLinkUrl;
  if (showPaymentLink) {
    const paymentBlockHeight = 4 + 9 + 5 + 14 + 6;
    if (extraContentY + paymentBlockHeight > pageHeight - FOOTER_RESERVE) {
      doc.addPage();
      extraContentY = margin;
    }
    doc.setDrawColor(200, 200, 200);
    doc.setLineWidth(0.3);
    doc.line(margin, extraContentY - 2, rightX, extraContentY - 2);

    const btnLabel = "Pay Online";
    const btnW = 60;
    const btnH = 9;
    const btnX = pageWidth / 2 - btnW / 2;
    const btnY = extraContentY + 4;
    doc.setFillColor(accentRgb[0], accentRgb[1], accentRgb[2]);
    doc.roundedRect(btnX, btnY, btnW, btnH, 2, 2, "F");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.setTextColor(255, 255, 255);
    doc.textWithLink(btnLabel, pageWidth / 2, btnY + 6, { align: "center", url: invoice.stripePaymentLinkUrl! });

    const cardIconY = btnY + btnH + 5;
    const logoW = 71;
    const logoH = 14;
    const logoX = pageWidth / 2 - logoW / 2;

    try {
      const logoResp = await fetch("/payment-card-logos.png");
      const logoBlob = await logoResp.blob();
      const logoDataUrl = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(logoBlob);
      });
      doc.addImage(logoDataUrl, "PNG", logoX, cardIconY, logoW, logoH);
    } catch {
      const fallbackLabels = ["VISA", "MC", "AMEX", "DISC"];
      const fallbackColors = ["#1a1f71", "#eb001b", "#007bc1", "#e87722"];
      const fW = 14;
      const fH = 9;
      const fGap = 3;
      const totalFW = fallbackLabels.length * fW + (fallbackLabels.length - 1) * fGap;
      let fX = pageWidth / 2 - totalFW / 2;
      for (let i = 0; i < fallbackLabels.length; i++) {
        doc.setFillColor(fallbackColors[i]);
        doc.roundedRect(fX, cardIconY, fW, fH, 1, 1, "F");
        doc.setFont("helvetica", "bold");
        doc.setFontSize(5);
        doc.setTextColor(255, 255, 255);
        doc.text(fallbackLabels[i], fX + fW / 2, cardIconY + fH / 2 + 1.5, { align: "center" });
        fX += fW + fGap;
      }
    }

    extraContentY = cardIconY + logoH + 6;
  }

  const invoiceTerms = invoice.termsAndNotes?.trim() || company.invoiceTermsAndNotes?.trim() || company.termsAndNotes?.trim() || "";
  if (invoiceTerms) {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8.5);
    const termsLines: string[] = doc.splitTextToSize(invoiceTerms, pageWidth - margin * 2);
    const lineHeight = 4.2;
    const headerHeight = 11;
    const estimatedHeight = headerHeight + termsLines.length * lineHeight;
    if (extraContentY + estimatedHeight > pageHeight - FOOTER_RESERVE) {
      doc.addPage();
      extraContentY = margin;
    }
    doc.setDrawColor(200, 200, 200);
    doc.setLineWidth(0.3);
    doc.line(margin, extraContentY - 2, rightX, extraContentY - 2);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.setTextColor(30, 30, 30);
    doc.text("Notes / Terms", margin, extraContentY + 4);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8.5);
    doc.setTextColor(60, 60, 60);
    let lineY = extraContentY + 9;
    for (const line of termsLines) {
      if (lineY > pageHeight - FOOTER_RESERVE) {
        doc.addPage();
        lineY = margin;
      }
      doc.text(line, margin, lineY);
      lineY += lineHeight;
    }
  }

  const totalPages = doc.getNumberOfPages();
  const invoiceFooterText = company.invoicePdfFooter?.trim() ?? "Created with QuoTrak — Professional quoting & invoicing made simple.\nhttps://quotrak.com";
  const invoiceFooterY = pageHeight - 8;
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7.5);
    doc.setTextColor(150, 150, 150);
    doc.setDrawColor(210, 210, 210);
    doc.setLineWidth(0.3);
    doc.line(margin, invoiceFooterY - 3.5, rightX, invoiceFooterY - 3.5);
    if (invoiceFooterText) {
      doc.text(invoiceFooterText, margin, invoiceFooterY, { align: "left" });
    }
    doc.text(`Page ${i} of ${totalPages}`, rightX, invoiceFooterY, { align: "right" });
  }

  return doc;
}

export function buildInvoiceFilename(invoice: InvoiceData): string {
  const num = invoice.invoiceNumber ? `Invoice-${invoice.invoiceNumber}` : `Invoice-${invoice.id}`;
  const customer = (invoice.customerName || "").replace(/[^a-zA-Z0-9\s]/g, "").trim().replace(/\s+/g, "-");
  return `${num}${customer ? `-${customer}` : ""}.pdf`;
}

export async function exportInvoicePDF(invoice: InvoiceData, company: CompanySettings = {}): Promise<void> {
  const doc = await buildInvoicePDFDoc(invoice, company);
  doc.save(buildInvoiceFilename(invoice));
}

export async function previewInvoicePDF(invoice: InvoiceData, company: CompanySettings = {}): Promise<string> {
  const doc = await buildInvoicePDFDoc(invoice, company);
  return doc.output("bloburl") as unknown as string;
}

export async function invoicePDFBase64(invoice: InvoiceData, company: CompanySettings = {}): Promise<string> {
  const doc = await buildInvoicePDFDoc(invoice, company);
  const dataUri = doc.output("datauristring");
  return dataUri.split(",")[1] ?? "";
}

export function invoicePDFFilename(invoice: InvoiceData): string {
  const num = invoice.invoiceNumber ? invoice.invoiceNumber.trim() : `INV-${invoice.id}`;
  return `${num}.pdf`;
}

function buildInvoiceInternalPDFDoc(invoice: InvoiceData, company: CompanySettings = {}): jsPDF {
  const estCompatible: EstimateData = {
    ...invoice,
    estimateNumber: invoice.invoiceNumber,
    estimateDate: invoice.invoiceDate ?? undefined,
    validUntil: invoice.dueDate ?? undefined,
  };
  return buildInternalPDFDoc(estCompatible, company);
}

export function exportInvoiceInternalPDF(invoice: InvoiceData, company: CompanySettings = {}): void {
  const doc = buildInvoiceInternalPDFDoc(invoice, company);
  doc.save(`${buildInvoiceFilename(invoice).replace(".pdf", "")}-INTERNAL.pdf`);
}

export function previewInvoiceInternalPDF(invoice: InvoiceData, company: CompanySettings = {}): string {
  const doc = buildInvoiceInternalPDFDoc(invoice, company);
  return doc.output("bloburl") as unknown as string;
}

export type ReceiptPaymentData = {
  id: number;
  amount: number;
  paymentDate: string;
  paymentMethod?: string | null;
  notes?: string | null;
};

export type ReceiptInvoiceData = {
  invoiceNumber?: string | null;
  customerName?: string | null;
  customerPhone?: string | null;
  customerEmail?: string | null;
  customerAddressLine1?: string | null;
  customerAddressLine2?: string | null;
  customerCity?: string | null;
  customerState?: string | null;
  customerPostalCode?: string | null;
  customerCountry?: string | null;
  total: number;
};

const PAYMENT_METHOD_LABELS: Record<string, string> = {
  cash: "Cash",
  cheque: "Check",
  bank_transfer: "Bank Transfer",
  "e-transfer": "E-Transfer",
  credit_card: "Credit Card",
  online: "Online",
  other: "Other",
};

export function buildReceiptPDFDoc(
  invoice: ReceiptInvoiceData,
  payment: ReceiptPaymentData,
  paymentIndex: number,
  allPayments: ReceiptPaymentData[],
  company: CompanySettings = {},
): jsPDF {
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "letter" });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 18;
  const rightX = pageWidth - margin;

  const accentRgb = hexToRgb(company.invoiceAccentColor || company.accentColor || "#1e3a5f");
  const currencyLabel = company.invoiceCurrencyLabel?.trim() || company.currencyLabel || "USD";
  const fmt = (v: number) => formatCurrencyWithLabel(v, currencyLabel);
  const fmtDate = (dateStr: string) => formatDateWithTimezone(dateStr, company.timezone);

  let y = margin;

  const maxLogoW = 50;
  const maxLogoH = 28;
  let logoW = maxLogoW;
  let logoH = maxLogoH;

  if (company.logoBase64) {
    try {
      const props = doc.getImageProperties(company.logoBase64);
      const scale = Math.min(maxLogoW / props.width, maxLogoH / props.height);
      logoW = props.width * scale;
      logoH = props.height * scale;
      doc.addImage(company.logoBase64, "PNG", margin, y, logoW, logoH);
    } catch {}
  }

  doc.setFont("helvetica", "normal");
  doc.setFontSize(36);
  doc.setTextColor(30, 30, 30);
  doc.text("PAYMENT RECEIPT", rightX, y + 10, { align: "right" });

  const companyInfoY = y + 16;
  const companyLines: string[] = [];
  if (company.companyName) companyLines.push(company.companyName);
  buildAddressLines(company).forEach(l => companyLines.push(l));
  if (company.phone) companyLines.push(company.phone);
  if (company.website) companyLines.push(company.website);
  if (company.email) companyLines.push(company.email);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(8.5);
  doc.setTextColor(50, 50, 50);
  companyLines.forEach((line, i) => {
    if (i === 0) {
      doc.setFont("helvetica", "bold");
      doc.setFontSize(10);
    } else {
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8.5);
    }
    doc.text(line, rightX, companyInfoY + i * 4.8, { align: "right" });
  });

  y = Math.max(y + logoH + 6, companyInfoY + companyLines.length * 4.8 + 4);

  doc.setDrawColor(180, 180, 180);
  doc.setLineWidth(0.4);
  doc.line(margin, y, rightX, y);
  y += 8;

  const billToStartY = y;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(7.5);
  doc.setTextColor(120, 120, 120);
  doc.text("RECEIPT TO", margin, billToStartY);

  let btY = billToStartY + 5;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.setTextColor(20, 20, 20);
  doc.text(invoice.customerName || "—", margin, btY);
  btY += 5;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(60, 60, 60);
  if (invoice.customerPhone) { doc.text(invoice.customerPhone, margin, btY); btY += 5; }
  if (invoice.customerEmail) { doc.text(invoice.customerEmail, margin, btY); btY += 5; }
  if (invoice.customerAddressLine1) { doc.text(invoice.customerAddressLine1, margin, btY); btY += 5; }
  if (invoice.customerAddressLine2) { doc.text(invoice.customerAddressLine2, margin, btY); btY += 5; }
  const rcptCityState = [invoice.customerCity, invoice.customerState].filter(Boolean).join(", ");
  const rcptPostalCountry = [invoice.customerPostalCode, invoice.customerCountry].filter(Boolean).join(" ");
  if (rcptCityState) { doc.text(rcptCityState, margin, btY); btY += 5; }
  if (rcptPostalCountry) { doc.text(rcptPostalCountry, margin, btY); btY += 5; }

  const metaRows: Array<{ label: string; value: string; highlight?: boolean }> = [];
  if (invoice.invoiceNumber) {
    metaRows.push({ label: "Invoice Number:", value: `#${invoice.invoiceNumber}` });
  }
  metaRows.push({ label: "Receipt:", value: `Payment ${paymentIndex}` });
  metaRows.push({ label: "Payment Date:", value: fmtDate(payment.paymentDate) });
  metaRows.push({ label: "Amount Paid:", value: fmt(payment.amount), highlight: true });

  const metaRowH = 7;
  const metaX = pageWidth / 2 + 10;
  const metaColW = rightX - metaX;
  metaRows.forEach((row, i) => {
    const ry = billToStartY + i * metaRowH;
    if (row.highlight) {
      doc.setFillColor(240, 240, 240);
      doc.rect(metaX - 2, ry - 4.5, metaColW + 2, metaRowH, "F");
    }
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8.5);
    doc.setTextColor(30, 30, 30);
    doc.text(row.label, rightX - 40, ry, { align: "right" });
    doc.setFont("helvetica", row.highlight ? "bold" : "normal");
    doc.setFontSize(8.5);
    doc.text(row.value, rightX, ry, { align: "right" });
  });

  y = Math.max(btY + 4, billToStartY + metaRows.length * metaRowH + 4);
  y += 6;

  const totalPaid = allPayments.reduce((s, p) => s + p.amount, 0);
  // Use a half-cent tolerance to absorb floating-point residues — avoids
  // showing "partial payment — remaining balance: $0.00" on a paid invoice.
  const rawBalance = Math.max(0, invoice.total - totalPaid);
  const balanceOwing = rawBalance < 0.005 ? 0 : rawBalance;
  const isPartial = balanceOwing > 0;

  const methodLabel = payment.paymentMethod
    ? (PAYMENT_METHOD_LABELS[payment.paymentMethod] ?? (payment.paymentMethod.charAt(0).toUpperCase() + payment.paymentMethod.slice(1)))
    : "—";

  const tableBody: string[][] = [];
  tableBody.push(["Invoice Total", fmt(invoice.total)]);
  tableBody.push(["Amount Paid (this payment)", fmt(payment.amount)]);
  tableBody.push(["Payment Method", methodLabel]);
  tableBody.push(["Payment Date", fmtDate(payment.paymentDate)]);
  if (allPayments.length > 1) {
    tableBody.push(["Total Payments Applied", fmt(totalPaid)]);
  }
  tableBody.push(["Balance Owing", isPartial ? fmt(balanceOwing) : "Paid in Full"]);

  autoTable(doc, {
    startY: y,
    body: tableBody,
    margin: { left: margin, right: margin, bottom: FOOTER_RESERVE },
    styles: {
      fontSize: 10,
      cellPadding: { top: 4, bottom: 4, left: 6, right: 6 },
      textColor: [30, 30, 30],
      lineColor: [220, 220, 220],
      lineWidth: 0,
    },
    columnStyles: {
      0: { cellWidth: "auto", fontStyle: "bold" as const, textColor: [80, 80, 80] as [number, number, number] },
      1: { cellWidth: 60, halign: "right" },
    },
    theme: "plain",
    didParseCell: (data: CellHookData) => {
      if (data.section === "body") {
        const isLastRow = data.row.index === tableBody.length - 1;
        const isAmountRow = data.row.index === 1;
        data.cell.styles.lineWidth = { top: 0, bottom: 0.2, left: 0, right: 0 };
        data.cell.styles.lineColor = [220, 220, 220];
        if (isLastRow) {
          data.cell.styles.fontSize = 11;
          if (data.column.index === 1) {
            data.cell.styles.textColor = [30, 30, 30] as [number, number, number];
            data.cell.styles.fontStyle = "bold";
          }
        }
        if (isAmountRow && data.column.index === 1) {
          data.cell.styles.fontStyle = "bold";
        }
      }
    },
  });

  let ty = (doc as any).lastAutoTable.finalY + 10;

  if (ty + 16 < pageHeight - FOOTER_RESERVE) {
    doc.setFont("helvetica", "italic");
    doc.setFontSize(8.5);
    doc.setTextColor(120, 120, 120);
    const confirmText = isPartial
      ? `This receipt confirms a partial payment of ${fmt(payment.amount)} toward invoice${invoice.invoiceNumber ? ` #${invoice.invoiceNumber}` : ""}. Remaining balance: ${fmt(balanceOwing)}.`
      : `This receipt confirms payment in full for invoice${invoice.invoiceNumber ? ` #${invoice.invoiceNumber}` : ""}. Thank you for your business.`;
    const confirmLines: string[] = doc.splitTextToSize(confirmText, rightX - margin);
    doc.text(confirmLines, margin, ty);
  }

  const footerText = company.invoicePdfFooter?.trim() || company.pdfFooter?.trim() || "Created with QuoTrak — Professional quoting & invoicing made simple.\nhttps://quotrak.com";
  const footerY = pageHeight - 8;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7.5);
  doc.setTextColor(150, 150, 150);
  doc.setDrawColor(210, 210, 210);
  doc.setLineWidth(0.3);
  doc.line(margin, footerY - 3.5, rightX, footerY - 3.5);
  if (footerText) {
    doc.text(footerText, margin, footerY, { align: "left" });
  }
  doc.text("Page 1 of 1", rightX, footerY, { align: "right" });

  return doc;
}

export function previewReceiptPDF(
  invoice: ReceiptInvoiceData,
  payment: ReceiptPaymentData,
  paymentIndex: number,
  allPayments: ReceiptPaymentData[],
  company: CompanySettings = {},
): string {
  const doc = buildReceiptPDFDoc(invoice, payment, paymentIndex, allPayments, company);
  return doc.output("bloburl") as unknown as string;
}

export function exportReceiptPDF(
  invoice: ReceiptInvoiceData,
  payment: ReceiptPaymentData,
  paymentIndex: number,
  allPayments: ReceiptPaymentData[],
  company: CompanySettings = {},
): void {
  const doc = buildReceiptPDFDoc(invoice, payment, paymentIndex, allPayments, company);
  doc.save(receiptPDFFilename(invoice, paymentIndex));
}

export function receiptPDFFilename(invoice: ReceiptInvoiceData, paymentIndex: number): string {
  const num = invoice.invoiceNumber ? `INV-${invoice.invoiceNumber}` : "Receipt";
  return `Receipt-${num}-Payment${paymentIndex}.pdf`;
}

export async function exportBulkZip(
  estimates: EstimateData[],
  type: "customer" | "internal" | "both",
  company: CompanySettings = {}
): Promise<void> {
  const zip = new JSZip();

  for (const estimate of estimates) {
    if (type === "customer" || type === "both") {
      const doc = buildCustomerPDFDoc(estimate, company);
      const blob = doc.output("blob");
      zip.file(buildPdfFilename(estimate), blob);
    }
    if (type === "internal" || type === "both") {
      const doc = buildInternalPDFDoc(estimate, company);
      const blob = doc.output("blob");
      zip.file(buildPdfFilename(estimate, "INTERNAL"), blob);
    }
  }

  const content = await zip.generateAsync({ type: "blob" });
  const url = URL.createObjectURL(content);
  const a = document.createElement("a");
  a.href = url;
  a.download = "quotes-export.zip";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
